import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { UsersService } from '../../shared/services/users.service';
import { AuthService } from './auth.service';
import { User } from './user.model';
import { MixpanelService } from '../../shared/services/mixpanel.service';
import { MixpanelEvents } from '../../shared/analytics/mixpanel-events';
import { trackMixpanelFailure } from '../../shared/analytics/mixpanel-tracking.utils';

const STATE_KEY = 'okta_state';
const NONCE_KEY = 'okta_nonce';
const VERIFIER_KEY = 'okta_code_verifier';
const RETURN_URL_KEY = 'okta_return_url';

export interface OktaCallbackPayload {
  code: string;
  state: string;
  redirectUri: string;
  codeVerifier: string;
  nonce: string;
}

@Injectable({
  providedIn: 'root',
})
export class OktaAuthService {

  constructor(
    private usersService: UsersService,
    private authService: AuthService,
    private router: Router,
    private mixpanel: MixpanelService,
  ) {
  }

  /** Whether SSO is wired for this build (`environment.okta.enabled`). */
  isEnabled(): boolean {
    return !!environment.okta?.enabled;
  }

  async startLogin(returnUrl?: string | null): Promise<void> {
    if (!this.isEnabled()) {
      throw new Error('Okta SSO is not enabled');
    }

    const verifier = this.generateRandomString(64);
    const challenge = await this.generateCodeChallenge(verifier);
    const state = this.generateRandomString(32);
    const nonce = this.generateRandomString(32);

    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(NONCE_KEY, nonce);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    } else {
      sessionStorage.removeItem(RETURN_URL_KEY);
    }

    const params = new URLSearchParams({
      client_id: environment.okta.clientId,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: environment.okta.redirectUri,
      state,
      nonce,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${environment.okta.issuer}/v1/authorize?${params.toString()}`;
  }

  hasPendingCallback(): boolean {
    return !!new URLSearchParams(window.location.search).get('code');
  }

  handleCallbackIfPresent(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      this.clearOauthQueryParams();
      this.clearSessionKeys();
      return Promise.reject(new Error(params.get('error_description') || error));
    }

    if (!code || !state) {
      return Promise.resolve(false);
    }

    const expectedState = sessionStorage.getItem(STATE_KEY);
    const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
    const nonce = sessionStorage.getItem(NONCE_KEY);

    if (!expectedState || state !== expectedState || !codeVerifier || !nonce) {
      this.clearOauthQueryParams();
      this.clearSessionKeys();
      return Promise.reject(new Error('Invalid Okta login state. Please try again.'));
    }

    const payload: OktaCallbackPayload = {
      code,
      state,
      redirectUri: environment.okta.redirectUri,
      codeVerifier,
      nonce,
    };

    return new Promise((resolve, reject) => {
      this.usersService.oktaCallback(payload).subscribe({
        next: (user: User) => {
          this.clearSessionKeys();
          this.clearOauthQueryParams();
          this.authService.establishSessionFromUser(user).subscribe({
            next: (authenticatedUser: User) => {
              if (authenticatedUser?.email) {
                this.mixpanel.identify(authenticatedUser.email);
                this.mixpanel.track(MixpanelEvents.LoginSucceeded, { auth_method: 'okta_sso' });
              }
              this.navigateAfterLogin(authenticatedUser);
              resolve(true);
            },
            error: (err) => {
              trackMixpanelFailure(this.mixpanel, MixpanelEvents.LoginFailed, { auth_method: 'okta_sso' }, err);
              reject(err);
            },
          });
        },
        error: (err) => {
          this.clearSessionKeys();
          this.clearOauthQueryParams();
          trackMixpanelFailure(this.mixpanel, MixpanelEvents.LoginFailed, { auth_method: 'okta_sso' }, err);
          reject(err);
        },
      });
    });
  }

  private navigateAfterLogin(user: User): void {
    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    if (user?.roles?.length > 1) {
      this.router.navigate(['/select']);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private clearSessionKeys(): void {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(NONCE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
  }

  private clearOauthQueryParams(): void {
    const hash = window.location.hash || '#/login';
    window.history.replaceState({}, document.title, `${window.location.pathname}${hash}`);
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    return Array.from(randomValues, (value) => charset[value % charset.length]).join('');
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const data = new TextEncoder().encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
